interface TransitionOptions {
    target: HTMLElement;
    toggled?: boolean;
    hideClass?: string;
    cancel?: boolean;

    enterFrom?: string | string[];
    enterActive?: string | string[];
    enterTo?: string | string[];
    leaveFrom?: string | string[];
    leaveActive?: string | string[];
    leaveTo?: string | string[];

    beforeEnter?(): void;
    enter?(): void;
    afterEnter?(): void;
    beforeLeave?(): void;
    leave?(): void;
    afterLeave?(): void;
}

interface Transition {
    toggled: boolean;
    enter(): void;
    leave(): void;
    toggle(override?: boolean): void;
}

const EVENT_END = 'transitionend';
const HIDE_CLASS = 'hidden';

function toArray(input: string | string[] | undefined | null): string[] {
    if (input == null) {
        return [];
    } else if (typeof input == 'string') {
        return input.split(' ');
    }

    return input;
}

function toggleClass(el: Element, classes: string[], state: boolean) {
    if (classes == null || !classes?.length)
        return;

    if (state) {
        classes.forEach((x) => el.classList.add(x));
    } else {
        classes.forEach((x) => el.classList.remove(x));
    }
}

export function createTransition(opts: TransitionOptions): Transition {
    const { target, beforeEnter, enter, afterEnter, beforeLeave, leave, afterLeave } = opts;
    const enterFrom = toArray(opts?.enterFrom);
    const enterActive = toArray(opts?.enterActive);
    const enterTo = toArray(opts?.enterTo);
    const leaveFrom = toArray(opts?.leaveFrom);
    const leaveActive = toArray(opts?.leaveActive);
    const leaveTo = toArray(opts?.leaveTo);
    const hideClass = opts?.hideClass ?? HIDE_CLASS;
    const cancel = Boolean(opts?.cancel ?? true);
    let toggled = opts?.toggled ?? !target.classList.contains(hideClass);
    let transitioning = false;

    function transitionLeave(): void {
        target.removeEventListener(EVENT_END, transitionLeave, false);
        transitioning = false;

        if (toggled) {
            toggleClass(target, enterActive, false);
            toggleClass(target, enterTo, false);
            afterEnter?.();
        } else {
            toggleClass(target, leaveActive, false);
            toggleClass(target, leaveTo, false);
            target.classList.add(hideClass);
            afterLeave?.();
        }
    }

    function transitionActive(): void {
        if (toggled) {
            enter?.();
            toggleClass(target, enterFrom, false);
            toggleClass(target, enterTo, true);
        } else {
            leave?.();
            toggleClass(target, leaveFrom, false);
            toggleClass(target, leaveTo, true);
        }
    }

    function transitionEnter(state: boolean): void {
        if (state === toggled) {
            // Already toggled, early exit
            return;
        } else if (transitioning) {
            if (!cancel) {
                // Cancelation not allowed, early exit
                return;
            }

            const classes = toggled
                ? [...enterFrom, ...enterActive, ...enterTo]
                : [...leaveFrom, ...leaveActive, ...leaveTo];

            toggleClass(target, classes, false);
            target.removeEventListener(EVENT_END, transitionLeave, false);
        }

        toggled = state;
        transitioning = true;
        target.addEventListener(EVENT_END, transitionLeave, false);

        if (toggled) {
            beforeEnter?.();
            target.classList.remove(hideClass);
            toggleClass(target, enterFrom, true);
            toggleClass(target, enterActive, true);
        } else {
            beforeLeave?.();
            toggleClass(target, leaveFrom, true);
            toggleClass(target, leaveActive, true);
        }

        enter?.();
        requestAnimationFrame(transitionActive);
    }

    function transitionToggle(override?: boolean): void {
        const state = override ?? !toggled;

        // Force current state
        if (override != null) {
            toggled = !override;
        }

        transitionEnter(state);
    }

    // Set initial hidden state if not toggled
    if (toggled) {
        target.classList.remove(hideClass);
    } else {
        target.classList.add(hideClass);
    }

    // Create transition orchestrator
    return Object.defineProperties(Object.create(null), {
        toggled: {
            get: () => toggled,
        },
        enter: {
            value: () => transitionEnter(true),
        },
        leave: {
            value: () => transitionEnter(false),
        },
        toggle: {
            value: transitionToggle,
        },
    });
}